import { toast } from "react-toastify";
import { renderToString } from "react-dom/server";

import { MessageComponentWithTitle } from "@stadium/services/react-toastify/utils";

import {
  TOAST_TYPES,
  DEFAULT_MESSAGE,
  PERSISTING_FLASH_CONFIG,
  DEFAULT_ERROR_TITLE,
  DEFAULT_SUCCESS_TITLE,
} from "@stadium/services/react-toastify/constants";

import "./assets/stylesheets/style.scss";

const defaultObj = {};
const { success, info, warning, error, defaultType } = TOAST_TYPES;

const showToast = ({ type, message = "", config = defaultObj } = defaultObj) => {
  let finalConfig = config;
  let toastId = null;
  if (typeof message === "string") {
    // toastId added to prevent duplicate flash messages
    toastId = message.split(" ").join("-");
  } else {
    // when message is a react component
    toastId = renderToString(message).split().join("-");
  }
  finalConfig = { toastId, ...finalConfig };

  if (type === defaultType) {
    return toast(message, finalConfig);
  }
  return toast[type](message, finalConfig);
};

export const showToastWithTitle = ({
  type,
  message = "",
  title,
  config = defaultObj,
} = defaultObj) =>
  showToast({
    type,
    message: <MessageComponentWithTitle type={type} title={title} message={message} />,
    config,
  });

export const showSuccessToast = ({
  message = "",
  title = DEFAULT_SUCCESS_TITLE,
  config = defaultObj,
} = defaultObj) =>
  showToastWithTitle({
    type: success,
    title: title || DEFAULT_SUCCESS_TITLE,
    message,
    config,
  });

export const showInfoToast = ({ message = "", title, config = defaultObj } = defaultObj) => {
  if (title) {
    return showToastWithTitle({
      type: info,
      title,
      message,
      config,
    });
  }
  return showToast({ type: info, message, config });
};

export const showWarningToast = ({ message = "", title, config = defaultObj } = defaultObj) => {
  if (title) {
    return showToastWithTitle({
      type: warning,
      title,
      message,
      config,
    });
  }
  return showToast({ type: warning, message, config });
};

export const showErrorToast = ({
  message = DEFAULT_MESSAGE,
  title = DEFAULT_ERROR_TITLE,
  config = defaultObj,
} = defaultObj) =>
  showToastWithTitle({
    type: error,
    title: title || DEFAULT_ERROR_TITLE,
    message,
    config,
  });

export const showDefaultToast = ({ message = "", title, config = defaultObj } = defaultObj) => {
  if (title) {
    return showToastWithTitle({
      type: defaultType,
      title,
      message,
      config,
    });
  }

  return showToast({ type: defaultType, message, config });
};

export const showPersistentErrorToast = ({ message, title, config = defaultObj } = defaultObj) =>
  showErrorToast({ message, title, config: { ...PERSISTING_FLASH_CONFIG, ...config } });

export const clearAllToasts = () => toast.dismiss();

export const clearSelectedToast = (toastRef) => toast.dismiss(toastRef);

const getTitle = (type, _title) => {
  if (type === success) {
    return DEFAULT_SUCCESS_TITLE;
  }

  if (type === error) {
    return DEFAULT_ERROR_TITLE;
  }

  return _title;
};

// Alias methods to support old implementation -- START
// Don't use these alias methods, these are only created to support the old implementations
export const generateMsg = (type = info, message, _title, config = defaultObj) =>
  showToastWithTitle({ type, message, title: getTitle(type, _title), config });

export const GenerateMsg = (type = info, message, _title, config = defaultObj) =>
  generateMsg(type, message, _title, config);

export const showError = (message, _title, config) => showErrorToast({ message, config });

export const flashError = (...params) => generateMsg(error, ...params);

export const showSuccess = (message, _title, config) => showSuccessToast({ message, config });

export const flashSuccess = (...params) => generateMsg(success, ...params);
// Alias methods -- END
